import { memo, useMemo } from "react";
import {
    Background,
    Controls,
    Edge,
    Handle,
    MarkerType,
    MiniMap,
    Node,
    NodeProps,
    Position,
    ReactFlow
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import { GraphNode, GraphPayload, GraphSourceOrigin } from "../../api";
import styles from "./EvidenceGraph.module.css";
import { clinicalLabel, getNodeProperty, groupClinicalNodes, safeEdgeLabel, SOURCE_COLORS, sourceOriginLabel } from "./graphPresentation";

interface RendererProps {
    payload: GraphPayload;
    onCitationClicked?: (citationFilePath: string) => void;
}

interface ClinicalNodeData extends Record<string, unknown> {
    graphNode: GraphNode;
    onCitationClicked?: (citationFilePath: string) => void;
}

type ClinicalFlowNode = Node<ClinicalNodeData, "clinicalNode">;

const NODE_WIDTH = 220;
const COLUMN_GAP = 310;
const ROW_GAP = 132;

export function ClinicalMapRenderer({ payload, onCitationClicked }: RendererProps) {
    const { nodes, edges } = useMemo(() => toClinicalFlow(payload, onCitationClicked), [payload, onCitationClicked]);

    if (!nodes.length) {
        return <p className={styles.mutedText}>No visible evidence matches the current filters.</p>;
    }

    return (
        <div className={styles.flowCanvas} aria-label="Clinical evidence map">
            <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={clinicalNodeTypes}
                fitView
                fitViewOptions={{ padding: 0.24 }}
                minZoom={0.35}
                maxZoom={1.7}
                nodesDraggable
                nodesConnectable={false}
                elementsSelectable
            >
                <MiniMap
                    pannable
                    zoomable
                    nodeColor={node => {
                        const graphNode = (node as ClinicalFlowNode).data.graphNode;
                        return SOURCE_COLORS[(graphNode.source_origin ?? "context_graph") as GraphSourceOrigin];
                    }}
                />
                <Controls showInteractive={false} />
                <Background color="#edebe9" gap={18} />
            </ReactFlow>
        </div>
    );
}

const clinicalNodeTypes = {
    clinicalNode: memo(ClinicalNode)
};

function ClinicalNode({ data, selected }: NodeProps<ClinicalFlowNode>) {
    const node = data.graphNode;
    const origin = (node.source_origin ?? "context_graph") as GraphSourceOrigin;
    const date = getNodeProperty(node, "assessment_date");
    const dateLabel = date == null ? undefined : String(date).slice(0, 10);
    return (
        <div className={`${styles.flowNode} ${selected ? styles.flowNodeSelected : ""}`}>
            <Handle type="target" position={Position.Left} className={styles.flowHandle} />
            <Handle type="source" position={Position.Right} className={styles.flowHandle} />
            <div className={styles.nodeTileHeader}>
                <span className={`${styles.sourceDot} ${styles[`sourceDot_${origin}`]}`} />
                <span className={styles.nodeType}>{clinicalLabel(node)}</span>
                <span className={styles.originBadge}>{sourceOriginLabel(origin)}</span>
            </div>
            <div className={styles.nodeLabel}>{node.label}</div>
            {dateLabel && <div className={styles.nodeMeta}>{dateLabel}</div>}
            {node.assessment_id && (
                <button
                    type="button"
                    className={`${styles.openNoteButton} nodrag nopan`}
                    onClick={event => {
                        event.stopPropagation();
                        data.onCitationClicked?.(node.assessment_id!);
                    }}
                >
                    Open note
                </button>
            )}
        </div>
    );
}

function toClinicalFlow(payload: GraphPayload, onCitationClicked?: (citationFilePath: string) => void) {
    const grouped = groupClinicalNodes(payload.nodes);
    const columnById = new Map<string, number>();
    [...grouped.patient, ...grouped.notes, ...grouped.context].forEach((node, index) => {
        const column = grouped.patient.includes(node) ? 0 : grouped.notes.includes(node) ? 1 : 2;
        columnById.set(node.id, column);
    });

    const rankInColumn = new Map<number, number>();
    const flowNodes: ClinicalFlowNode[] = payload.nodes.map((node, index) => {
        const column = columnById.get(node.id) ?? 2;
        const rank = rankInColumn.get(column) ?? 0;
        rankInColumn.set(column, rank + 1);
        return {
            id: node.id,
            type: "clinicalNode",
            data: { graphNode: node, onCitationClicked },
            position: {
                x: column * COLUMN_GAP,
                y: rank * ROW_GAP + (column === 1 ? 36 : 0)
            },
            width: NODE_WIDTH,
            ariaLabel: `${clinicalLabel(node)} ${node.label}`,
            selected: index === 0
        };
    });

    const flowEdges: Edge[] = payload.edges.map(edge => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        label: safeEdgeLabel(edge),
        animated: edge.source_origin === "both",
        style: { stroke: SOURCE_COLORS[(edge.source_origin ?? "context_graph") as GraphSourceOrigin], strokeWidth: 1.5 },
        markerEnd: { type: MarkerType.ArrowClosed },
        labelStyle: { fill: "#605e5c", fontSize: 11 },
        labelBgPadding: [6, 3],
        labelBgBorderRadius: 3,
        labelBgStyle: { fill: "#ffffff", fillOpacity: 0.86 }
    }));

    return { nodes: flowNodes, edges: flowEdges };
}
